import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import formatPhoneNumber from "../phoneUtils";
import { getInitials } from "../../utils/helpers";
import { getFirstEmail, getAllEmails } from "../../utils/emailHelpers";


// 2. Ligne de tableau individuel
export const IndividualRow = React.memo(({ 
  individual, 
  isSelected, 
  onSelect, 
  onClick, 
  onDelete,
  getTagStyle,
  screenSize = 'desktop'
}) => {
  const handleClick = useCallback(() => onClick(individual._id), [individual._id, onClick]);
  const handleSelect = useCallback((e) => {
    e.stopPropagation();
    onSelect(individual._id);
  }, [individual._id, onSelect]);
  const handleDelete = useCallback((e) => {
    e.stopPropagation();
    onDelete(individual._id);
  }, [individual._id, onDelete]);

  // Vue Mobile - Carte
  if (screenSize === 'mobile') {
    return (
      <div className={`bg-white rounded-lg shadow-sm border p-4 transition duration-200 ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
      >
        {/* Header avec checkbox et bouton supprimer */}
        <div className="flex items-center justify-between mb-4">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleSelect}
            className="w-4 h-4 text-indigo-600 bg-white border-gray-300 rounded focus:ring-indigo-500 focus:ring-2"
          />
          
          <button
            onClick={handleDelete}
            className="text-red-500 hover:text-red-700 p-1"
            aria-label="Delete individual"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* Photo et informations principales */}
        <div className="flex flex-col items-center text-center mb-4">
          {/* Photo de profil */}
          <div className="mb-3">
            {individual.photo ? (
              <img 
                src={individual.photo} 
                alt={`${individual.Name} profile`}
                className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            {/* Avatar par défaut si pas de photo ou erreur de chargement */}
            <div
              className={`w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-xl ${individual.photo ? 'hidden' : 'flex'}`}
            >
              {getInitials(individual.Name)}
            </div>
          </div>
          
          {/* Nom et entreprise */}
          <div className="space-y-1">
            <button
              className="text-lg font-semibold text-blue-600 hover:text-blue-800 block"
            >
              {individual.Name}
            </button>
            
            {individual.company && (
              <p className="text-sm text-gray-600">{individual.company}</p>
            )}
          </div>
        </div>

        <div className="space-y-2 text-sm">
          {individual.phoneNumber && (
            <div className="flex items-center space-x-2">
              <span className="text-gray-500 min-w-0">📞</span>
              <span className="truncate">{formatPhoneNumber(individual.phoneNumber)}</span>
            </div>
          )}

          {(getAllEmails(individual.professionalEmail).length > 0 || getAllEmails(individual.personalEmail).length > 0) && (
            <div className="flex items-start space-x-2">
              <span className="text-gray-500 min-w-0 mt-0.5">✉️</span>
              <div className="flex flex-col space-y-1 flex-1 min-w-0">
                {getAllEmails(individual.professionalEmail).length > 0 && (
                  <span className="truncate text-blue-600" title={getFirstEmail(individual.professionalEmail)}>
                    {getFirstEmail(individual.professionalEmail)}
                  </span>
                )}
                {getAllEmails(individual.personalEmail).length > 0 && (
                  <span className="truncate text-gray-600" title={getFirstEmail(individual.personalEmail)}>
                    {getFirstEmail(individual.personalEmail)}
                  </span>
                )}
              </div>
            </div>
          )}

          {individual.city && (
            <div className="flex items-center space-x-2">
              <span className="text-gray-500 min-w-0">📍</span>
              <span className="truncate">{individual.city.split(",")[0].trim()}</span>
            </div>
          )}
        </div>

        {individual.tags && individual.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {individual.tags.slice(0, 3).map((tag) => (
              <span
                key={tag._id}
                className={`px-2 py-1 text-xs rounded-full ${getTagStyle(tag)}`}
              >
                {tag.name}
              </span>
            ))}
            {individual.tags.length > 3 && (
              <span className="text-xs text-gray-500 py-1">+{individual.tags.length - 3}</span>
            )}
          </div>
        )}

        <div className="flex justify-between items-center mt-3 pt-3 border-t text-xs text-gray-500">
          <span>Ajouté le {new Date(individual.createdAt).toLocaleDateString()}</span>
        </div>
      </div>
    );
  }

  // Vue Table responsive pour tablette et desktop
  return (
    <tr 
      id={`individual-row-${individual._id}`} 
      className={`group transition-all cursor-pointer duration-200 border-b border-gray-100 hover:bg-gray-50 hover:shadow-sm ${
        isSelected ? 'bg-indigo-50 border-indigo-200' : ''
      }`}
      onClick={handleClick}

    >
      <td className="px-3 py-4 w-12" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={handleSelect}
          className="w-4 h-4 text-indigo-600 bg-white border-gray-300 rounded focus:ring-indigo-500 focus:ring-2"
        />
      </td>
      <td className="px-2 py-4 w-80 max-w-80">
        <div className="flex items-start space-x-2">
          {/* Photo de profil */}
          <div className="flex-shrink-0 -mt-1">
            {individual.photo ? (
              <img
                src={individual.photo}
                alt={`${individual.Name} profile`}
                className="w-14 h-14 rounded-full object-cover border border-gray-200"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            {/* Avatar par défaut si pas de photo ou erreur de chargement */}
            <div
              className={`w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm ${individual.photo ? 'hidden' : 'flex'}`}
            >
              {getInitials(individual.Name)}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            {/* Nom avec logo LinkedIn */}
            <div className="flex items-center gap-1.5">
              <span className="text-indigo-600 font-medium truncate">
                {individual.Name}
              </span>
              {individual.linkedinUrl && (
                <a
                  href={individual.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 text-blue-600 hover:text-blue-800 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                  title="Voir le profil LinkedIn"
                >
                  <i className="bi bi-linkedin text-sm"></i>
                </a>
              )}
            </div>

            {/* JobTitle et Company sur la même ligne */}
            {(individual.jobTitle || individual.company) && (
              <div className="flex items-center gap-1.5 mt-1">
                {individual.jobTitle && (
                  <span className="text-sm font-semibold text-gray-900 truncate">
                    {individual.jobTitle.length > 35 ? individual.jobTitle.slice(0, 35) + "..." : individual.jobTitle}
                  </span>
                )}
                {individual.jobTitle && individual.company && (
                  <span className="text-sm text-gray-400">-</span>
                )}
                {individual.company && (
                  <span className="text-sm text-gray-600 truncate">
                    {individual.company.length > 35 ? individual.company.slice(0, 35) + "..." : individual.company}
                  </span>
                )}
              </div>
            )}

            {/* City avec icône */}
            {individual.city && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <i className="bi bi-geo-alt text-gray-400 text-xs flex-shrink-0"></i>
                <span className="text-xs text-gray-500 truncate">
                  {individual.city.split(",")[0].trim()}
                </span>
              </div>
            )}

            {/* Formation avec icône */}
            {individual.formation && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <i className="bi bi-mortarboard text-gray-400 text-xs flex-shrink-0"></i>
                <span className="text-xs text-gray-500 truncate">
                  {individual.formation.length > 50 ? individual.formation.slice(0, 50) + "..." : individual.formation}
                </span>
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 w-72 max-w-72">
        {(getAllEmails(individual.professionalEmail).length > 0 || getAllEmails(individual.personalEmail).length > 0 || individual.phoneNumber) ? (
          <div className="flex flex-col space-y-1.5">
            {getAllEmails(individual.professionalEmail).length > 0 && (
              <div className="flex items-center gap-2">
                <i className="bi bi-envelope-fill text-blue-500 text-sm flex-shrink-0" title="Email professionnel"></i>
                <span
                  title={getFirstEmail(individual.professionalEmail)}
                  className="text-sm text-gray-700 truncate"
                >
                  {getFirstEmail(individual.professionalEmail)}
                </span>
              </div>
            )}
            {getAllEmails(individual.personalEmail).length > 0 && (
              <div className="flex items-center gap-2">
                <i className="bi bi-envelope-fill text-green-500 text-sm flex-shrink-0" title="Email personnel"></i>
                <span
                  title={getFirstEmail(individual.personalEmail)}
                  className="text-sm text-gray-700 truncate"
                >
                  {getFirstEmail(individual.personalEmail)}
                </span>
              </div>
            )}
            {individual.phoneNumber && (
              <div className="flex items-center gap-2">
                <i className="bi bi-telephone-fill text-gray-500 text-sm flex-shrink-0" title="Téléphone"></i>
                <span className="text-sm text-gray-700 truncate">
                  {formatPhoneNumber(individual.phoneNumber)}
                </span>
              </div>
            )}
          </div>
        ) : (
          <span className="text-sm text-gray-500">-</span>
        )}
      </td>


      {/* Actions - Affichées seulement sur desktop */}
      {screenSize === 'desktop' && (
        <td className="px-3 py-4 text-center w-16" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={handleDelete}
            className="p-2 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 transition-all duration-200"
            aria-label="Delete individual"
          >
            <i className="bi bi-trash text-sm"></i>
          </button>
        </td>
      )}

      {/* Actions sur tablette */}
      {screenSize === 'tablet' && (
        <td className="px-3 py-4 text-center w-16" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={handleDelete}
            className="p-2 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 transition-all duration-200"
            aria-label="Delete individual"
          >
            <i className="bi bi-trash text-sm"></i>
          </button>
        </td>
      )}
    </tr>
  );
});
